// src/hooks/useAutoRefresh.ts

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/api/client";

const BASELINE_INTERVAL_MS = 30_000;
const FAST_INTERVAL_MS = 5_000;

/** ~10 minutes of fast refreshes. A run stuck in STARTED must not keep a 5s
 *  poll alive forever; past the cap the hook degrades to the baseline cadence.
 *  The budget resets when activity stops and later resumes (a new run deserves
 *  a fresh window), and on an explicit boost(). Only ticks that actually start
 *  a refresh spend budget — ticks skipped on the in-flight guard don't. */
const MAX_FAST_TICKS = 120;

/** Fast ticks granted by boost(): ~60s. Covers the gap between triggering a
 *  run and the backend row actually showing it as active. */
const BOOST_TICKS = 12;

/** Session lost (401) or access revoked (403): stop for good. The API client
 *  already redirects to /login on a failed refresh; retrying only hammers. */
const STOP_STATUSES = new Set([401, 403]);

export type AutoRefreshOptions = {
  /** Master switch — e.g. `false` until the user/session is known. */
  enabled: boolean;
  /** True while the caller's data shows in-flight work → fast cadence. */
  fast: boolean;
  /**
   * Refetch callback. `fast` tells the caller which tier fired, so cheap
   * side-resources can be limited to baseline ticks. Errors thrown here are
   * treated as transport noise (retry next tick), except ApiError 401/403,
   * which stops the hook permanently.
   */
  refresh: (info: { fast: boolean }) => Promise<void>;
  baselineMs?: number;
  fastMs?: number;
  /**
   * Statuses that stop the hook for good. Default `[401, 403]`.
   *
   * Override it when a 404 means "this endpoint is not there" rather than
   * "this row is gone" — during a deploy the frontend can be newer than the
   * backend, and without this the poll hammers a 404 every tick. It is an
   * option and not a change to the default on purpose: `DataManagement`
   * rethrows every background error so THIS hook decides, and latching its
   * sources poll dead on one transient 404 would be the very bug we are
   * fixing elsewhere.
   */
  stopStatuses?: number[];
  /** Fast ticks before degrading to baseline. Default 120. */
  maxFastTicks?: number;
  /**
   * Change this to un-stop a hook that stopped, and to hand it a fresh fast
   * budget — e.g. the active account id, so switching account revives a poll
   * that a 403 killed on the way out of the previous one.
   *
   * Without it, `stopped` is a one-way door: a provider mounted at the app
   * root never unmounts, so one 403 disables it for the whole session with
   * no symptom other than the page quietly no longer updating.
   */
  resetKey?: unknown;
};

/**
 * Two-tier background refresh: a relaxed baseline tick while the page is
 * simply open, a fast tick while the caller reports in-flight activity.
 * Hidden tabs don't poll at all; returning to the tab refreshes immediately.
 *
 * Deliberately different from `useManagedSourcePoll` (single resource,
 * terminal-state semantics, hard overall cap): this hook is an ambient
 * page-level refresher that never finishes — only the fast tier is capped.
 */
export function useAutoRefresh(opts: AutoRefreshOptions): { boost: () => void } {
  const {
    enabled,
    fast,
    refresh,
    baselineMs = BASELINE_INTERVAL_MS,
    fastMs = FAST_INTERVAL_MS,
    maxFastTicks = MAX_FAST_TICKS,
    resetKey,
  } = opts;

  // Held in a ref, not a memo: callers pass an inline array, so a new
  // identity every render would tear down the interval on every render.
  const stopStatusesRef = useRef(opts.stopStatuses);
  stopStatusesRef.current = opts.stopStatuses;

  // A refresh that hangs longer than this no longer blocks new attempts — a
  // single never-settling request must not wedge the hook (and the page's
  // freshness) until reload.
  const staleInFlightMs = Math.max(baselineMs, fastMs) * 2;

  // Hidden tabs don't poll — same gate as useManagedSourcePoll.
  const [visible, setVisible] = useState(
    () => document.visibilityState !== "hidden",
  );
  const [stopped, setStopped] = useState(false);
  const [cappedOut, setCappedOut] = useState(false);
  const [boostTicks, setBoostTicks] = useState(0);

  // Callers pass `refresh` inline, so it's a new function every render.
  // Holding it in a ref keeps the interval from tearing down each render.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  });

  const inFlightRef = useRef(false);
  const refreshSeqRef = useRef(0);
  const lastStartRef = useRef(0);
  const fastTicksRef = useRef(0);

  // Activity flipping false → true is a new run: grant a fresh fast budget.
  const prevFastRef = useRef(fast);
  useEffect(() => {
    if (fast && !prevFastRef.current) {
      fastTicksRef.current = 0;
      setCappedOut(false);
    }
    prevFastRef.current = fast;
  }, [fast]);

  // `resetKey` changed: forget that we ever stopped, and start the fast
  // budget over. Skipped on mount — there is nothing to reset yet, and a
  // state write there would cost an extra render for nothing.
  const prevResetKeyRef = useRef(resetKey);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevResetKeyRef.current = resetKey;
      return;
    }
    if (Object.is(prevResetKeyRef.current, resetKey)) return;
    prevResetKeyRef.current = resetKey;
    fastTicksRef.current = 0;
    setCappedOut(false);
    setStopped(false);
  }, [resetKey]);

  useEffect(() => {
    const onVisibilityChange = () =>
      setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const fastMode = (fast || boostTicks > 0) && !cappedOut;
  const intervalMs = fastMode ? fastMs : baselineMs;

  // A response slower than the interval must not stack up requests — unless
  // it's been in flight so long we consider it hung (stale escape above).
  const blocked = useCallback(
    () =>
      inFlightRef.current &&
      Date.now() - lastStartRef.current < staleInFlightMs,
    [staleInFlightMs],
  );

  const runTick = useCallback(
    async (isFastTick: boolean) => {
      if (blocked()) return;
      // Sequence token: when a stale hung request finally settles, its
      // `finally` must not clear the guard out from under a newer request.
      const seq = ++refreshSeqRef.current;
      inFlightRef.current = true;
      lastStartRef.current = Date.now();
      try {
        await refreshRef.current({ fast: isFastTick });
      } catch (err) {
        const stops = stopStatusesRef.current;
        const isStop =
          err instanceof ApiError &&
          (stops ? stops.includes(err.status) : STOP_STATUSES.has(err.status));
        if (isStop) {
          setStopped(true);
        }
        // Anything else is transport noise: keep the last known data and
        // retry on the next tick.
      } finally {
        if (refreshSeqRef.current === seq) inFlightRef.current = false;
      }
    },
    [blocked],
  );

  useEffect(() => {
    if (!enabled || !visible || stopped) return;

    const interval = setInterval(() => {
      // A skipped tick spends no budget: only real refreshes count.
      if (blocked()) return;
      if (fastMode) {
        fastTicksRef.current += 1;
        if (fastTicksRef.current >= maxFastTicks) setCappedOut(true);
        setBoostTicks((b) => (b > 0 ? b - 1 : 0));
      }
      void runTick(fastMode);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [enabled, visible, stopped, fastMode, intervalMs, blocked, runTick]);

  // Coming back to the tab refreshes at once — the user shouldn't stare at
  // data that went stale while the tab was hidden and not polling. Throttled
  // to one refresh per fast interval so tab-flicker can't hammer the API.
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = visible;
    if (!visible || wasVisible) return;
    if (!enabled || stopped) return;
    if (Date.now() - lastStartRef.current < fastMs) return;
    void runTick(fastMode);
  }, [visible, enabled, stopped, fastMode, fastMs, runTick]);

  const boost = useCallback(() => {
    fastTicksRef.current = 0;
    setCappedOut(false);
    setBoostTicks(BOOST_TICKS);
  }, []);

  return { boost };
}
